import type { Operator } from '@eis/insuranceservice-sdk';

/**
 * The `filters` grammar on the console side (SDD-017 §5.1): the same language `pkg/listquery`
 * parses on the server, read into the same AST — `{and:[{or:[{field, op, value?|values?}]}]}`,
 * the shape `fixtures/golden/scenarios.list-search.json#grammar` states.
 *
 *  - `,` (OR) binds tighter than `;` (AND): `a==1;b==2,b==3` is `a=1 AND (b=2 OR b=3)`.
 *  - `\;` `\,` `\|` `\\` (and `\(` `\)`) escape a reserved character inside a value; any other
 *    backslash is the value's own, so a regex `\d` stays `\d`.
 *  - `==$null` / `!=$null` are operators in their own right and carry no value.
 *  - Anything it cannot read — a single `=`, an empty clause, a list that does not close — throws a
 *    {@link FilterParseError} with reason `PARSE`. An unreadable filter is never "no filter".
 *
 * Pure: no catalog, no I/O. Whether a field exists or accepts an operator is the catalog's
 * question, answered by the server (and, later, by `fromFilters()`).
 */

/** A `filters` operator, spelled as the grammar writes it (the SDK's {@link Operator}). */
export type FilterOperator = Operator;

/** A clause whose operator takes one value. */
export interface ValueClause {
  readonly field: string;
  readonly op: Exclude<FilterOperator, '=()' | '!()' | '==$null' | '!=$null'>;
  readonly value: string;
}

/** IN / NOT IN: a list of values. */
export interface ListClause {
  readonly field: string;
  readonly op: '=()' | '!()';
  readonly values: readonly string[];
}

/** `==$null` / `!=$null`: no value. */
export interface NullClause {
  readonly field: string;
  readonly op: '==$null' | '!=$null';
}

export type FilterClause = ValueClause | ListClause | NullClause;

/** A disjunction — the `,`-separated part. */
export interface FilterOr {
  readonly or: readonly FilterClause[];
}

/** A parsed `filters` value: a conjunction of disjunctions. Empty `and` matches every row. */
export interface FilterAst {
  readonly and: readonly FilterOr[];
}

/** The reasons a `filters` value is refused by the parser (the server's `ReasonParse`). */
export type FilterParseReason = 'PARSE';

/** Thrown by {@link parseFilters} for anything the grammar cannot read. */
export class FilterParseError extends Error {
  override readonly name = 'FilterParseError';
  readonly reason: FilterParseReason = 'PARSE';
  /** The field the unreadable clause names, when it got that far. */
  readonly field: string;
  constructor(field: string, message: string) {
    super(message);
    this.field = field;
  }
}

export function isFilterParseError(error: unknown): error is FilterParseError {
  return error instanceof FilterParseError;
}

const FIELD_KEY = /^[A-Za-z_][A-Za-z0-9_]*(\[\])?(\.[A-Za-z_][A-Za-z0-9_]*(\[\])?)*$/;

/** Longest first, so `>=` is never read as `>` followed by a value starting with `=`. */
const OPERATORS_BY_LENGTH = [
  '==',
  '!=',
  '>=',
  '<=',
  '=@',
  '!@',
  '=~',
  '!~',
  '=(',
  '!(',
  '>',
  '<',
] as const;

const isKeyChar = (c: string): boolean => /^[A-Za-z0-9_.[\]]$/.test(c);

/** Splits on `sep` wherever it is not escaped; the pieces keep their escapes. */
function splitUnescaped(s: string, sep: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i);
    if (c === '\\' && i + 1 < s.length) {
      current += c + s.charAt(i + 1);
      i++;
      continue;
    }
    if (c === sep) {
      parts.push(current);
      current = '';
      continue;
    }
    current += c;
  }
  parts.push(current);
  return parts;
}

/** Removes the backslash before a reserved character; any other backslash is the value's own. */
function unescape(s: string): string {
  if (!s.includes('\\')) return s;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i);
    if (c === '\\' && i + 1 < s.length && ';,|\\()'.includes(s.charAt(i + 1))) {
      out += s.charAt(i + 1);
      i++;
      continue;
    }
    out += c;
  }
  return out;
}

function parseClause(part: string): FilterClause {
  const text = part.trim();
  if (text === '') {
    throw new FilterParseError('', 'an empty clause: two separators in a row, or one at an end');
  }
  let end = 0;
  while (end < text.length && isKeyChar(text.charAt(end))) end++;
  const field = text.slice(0, end);
  if (field === '' || !FIELD_KEY.test(field)) {
    throw new FilterParseError(field, `"${text}" does not start with a field key`);
  }
  const rest = text.slice(end);
  const op = OPERATORS_BY_LENGTH.find((candidate) => rest.startsWith(candidate));
  if (op === undefined) {
    throw new FilterParseError(
      field,
      `"${text}" has no operator after "${field}"; operators are == != > < >= <= =@ !@ =~ !~ =(…) !(…) ==$null !=$null`,
    );
  }
  const expression = rest.slice(op.length);
  if (op === '=(' || op === '!(') {
    if (
      !expression.endsWith(')') ||
      (expression.endsWith('\\)') && !expression.endsWith('\\\\)'))
    ) {
      throw new FilterParseError(field, `"${text}" opens a list with ( and does not close it`);
    }
    const values = splitUnescaped(expression.slice(0, -1), '|').map(unescape);
    if (values.length === 1 && values[0] === '') {
      throw new FilterParseError(field, `"${text}" has an empty list`);
    }
    return { field, op: op === '=(' ? '=()' : '!()', values };
  }
  const value = unescape(expression);
  if (value === '$null' && op === '==') return { field, op: '==$null' };
  if (value === '$null' && op === '!=') return { field, op: '!=$null' };
  if (value === '') {
    throw new FilterParseError(
      field,
      `"${text}" has no value after ${op}; to ask for an absent value use ${op}$null`,
    );
  }
  return { field, op, value };
}

/**
 * Reads a `filters` value into its AST. An empty (or blank) value is the empty filter — and only
 * that is. Throws {@link FilterParseError} for anything unreadable.
 */
export function parseFilters(raw: string): FilterAst {
  if (raw.trim() === '') return { and: [] };
  return {
    and: splitUnescaped(raw, ';').map((group) => ({
      or: splitUnescaped(group, ',').map(parseClause),
    })),
  };
}

/** Escapes what the grammar reserves inside a value: `;` `,` `|` and `\`. */
export function escapeFilterLiteral(value: string): string {
  return value.replace(/[;,|\\]/g, (c) => `\\${c}`);
}

/** Renders one clause in the grammar, escaped. */
export function renderClause(clause: FilterClause): string {
  switch (clause.op) {
    case '==$null':
    case '!=$null':
      return `${clause.field}${clause.op}`;
    case '=()':
    case '!()':
      return `${clause.field}${clause.op === '=()' ? '=(' : '!('}${clause.values.map(escapeFilterLiteral).join('|')})`;
    default:
      return `${clause.field}${clause.op}${escapeFilterLiteral(clause.value)}`;
  }
}

/**
 * Renders an AST back in the grammar — the normal form: `parseFilters(renderFilters(ast))` is
 * `ast`, and `renderFilters(parseFilters(s))` is `s` without stray whitespace.
 */
export function renderFilters(ast: FilterAst): string {
  return ast.and.map((group) => group.or.map(renderClause).join(',')).join(';');
}
